import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import StorefrontIcon from '@mui/icons-material/StorefrontOutlined';
import { apiClient } from '@/api/axiosClient';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardLineChart from '@/components/dashboard/DashboardLineChart';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvTopList from '@/components/tvDashboard/TvTopList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { groupSumByMonth, topN } from '@/lib/dashboardAggregations';
import type { CustomerDispatchSummaryRow, DispatchOrderRegisterRow } from '@/types/reports';

export default function OutboundSlide() {
  const { t } = useTranslation('dashboard');
  const { t: tr } = useTranslation('reports');
  const colors = useCategoryColors();

  const { data: dispatchOrders = [] } = useQuery({
    queryKey: ['reports', 'dispatch-order-register'],
    queryFn: async () => (await apiClient.get<DispatchOrderRegisterRow[]>('/reports/dispatch-order-register')).data,
    refetchInterval: 60_000,
  });
  const { data: customerSummary = [] } = useQuery({
    queryKey: ['reports', 'customer-dispatch-summary'],
    queryFn: async () => (await apiClient.get<CustomerDispatchSummaryRow[]>('/reports/customer-dispatch-summary')).data,
    refetchInterval: 60_000,
  });

  const totalOrders = useMemo(() => new Set(dispatchOrders.map((r) => r.dispatchOrderId)).size, [dispatchOrders]);
  const issuedQty = useMemo(() => dispatchOrders.reduce((sum, r) => sum + r.issuedQty, 0), [dispatchOrders]);
  const pendingQty = useMemo(() => dispatchOrders.reduce((sum, r) => sum + r.pendingQty, 0), [dispatchOrders]);
  const openOrders = useMemo(() => customerSummary.reduce((sum, r) => sum + r.openOrders, 0), [customerSummary]);

  const topCustomers = useMemo(() => topN(customerSummary, (r) => r.totalRequestedQty, 5), [customerSummary]);
  const monthlyTrend = useMemo(() => groupSumByMonth(dispatchOrders, (r) => r.orderDate, [{ key: 'count', value: () => 1 }]), [dispatchOrders]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <TvKpiTile label={t('outbound.totalOrders')} value={totalOrders} icon={<LocalShippingIcon />} accentColor={colors.outbound} />
        <TvKpiTile label={t('outbound.issuedQty')} value={issuedQty.toLocaleString()} icon={<Inventory2Icon />} accentColor={colors.outbound} />
        <TvKpiTile label={t('outbound.openOrders')} value={openOrders} icon={<StorefrontIcon />} accentColor={colors.outbound} />
        <TvKpiTile label={tr('columns.pendingQty')} value={pendingQty.toLocaleString()} icon={<PendingActionsIcon />} accentColor={colors.outbound} />
      </Box>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 2, minHeight: 0 }}>
        <ChartCard title={t('outbound.trendTitle')} height={420}>
          <DashboardLineChart
            categories={monthlyTrend.map((m) => m.month)}
            series={[{ label: t('outbound.totalOrders'), data: monthlyTrend.map((m) => m.values.count), color: colors.outbound, area: true }]}
            height={420}
            legendPosition="bottom"
          />
        </ChartCard>
        <TvTopList title={t('outbound.topCustomersTitle')} accentColor={colors.outbound} items={topCustomers.map((c) => ({ label: c.customerName, value: c.totalRequestedQty }))} />
      </Box>
    </Box>
  );
}
